import { ArrowRight, Wrench, Shield, Settings, Calculator, LogOut } from 'lucide-react';
import { colors, Card, TopNav } from './shared';
import { useAuth } from '../contexts/AuthContext';

const AppCard = ({ icon: Icon, title, description, href, color = colors.yellow }) => (
  <a
    href={href}
    className="block p-6 rounded-2xl transition-all hover:scale-[1.02] active:scale-98"
    style={{ background: colors.cardBg, border: `1px solid ${colors.border}` }}
  >
    <div className="flex items-start justify-between mb-4">
      <div
        className="p-3 rounded-xl"
        style={{ background: `${color}20` }}
      >
        <Icon size={24} style={{ color }} />
      </div>
      <ArrowRight size={20} style={{ color: colors.textMuted }} />
    </div>
    <h3 className="text-lg font-bold mb-1" style={{ color: colors.textPrimary }}>{title}</h3>
    <p className="text-sm" style={{ color: colors.textSecondary }}>{description}</p>
  </a>
);

export default function LandingPage() {
  const { user, logout } = useAuth();

  const handleLogout = async () => {
    await logout();
    window.location.reload();
  };

  return (
    <div className="min-h-screen" style={{ background: colors.black }}>
      <TopNav currentPath="/" />

      <div className="max-w-6xl mx-auto px-6 py-12">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold mb-3" style={{ color: colors.textPrimary }}>
            Property Management Suite
          </h1>
          <p className="text-lg" style={{ color: colors.textSecondary }}>
            Tools for maintenance teams and property managers
          </p>
          {user && (
            <div className="mt-4 flex items-center justify-center gap-3">
              <span className="text-sm" style={{ color: colors.textMuted }}>
                Signed in as {user.email}
              </span>
              <button
                onClick={handleLogout}
                className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all hover:opacity-80"
                style={{ background: colors.redDim, color: colors.red }}
              >
                <LogOut size={16} />
                Logout
              </button>
            </div>
          )}
        </div>

        {/* Main Grid */}
        <div className="grid md:grid-cols-2 gap-8 mb-12">
          {/* User Apps Section */}
          <div>
            <h2 className="text-sm font-semibold uppercase tracking-wider mb-4 px-1" style={{ color: colors.textMuted }}>
              Applications
            </h2>
            <div className="space-y-4">
              <AppCard
                icon={Wrench}
                title="Trades App"
                description="Work order management, receipts, locks, inspections, and AI-powered tech assistance"
                href="/trades-app"
                color={colors.yellow}
              />
              <AppCard
                icon={Calculator}
                title="Insurance Estimator"
                description="Generate water damage mitigation and repair estimates with AI-assisted document parsing"
                href="/insurance-estimator"
                color={colors.green}
              />
            </div>
          </div>

          {/* Admin Section */}
          <div>
            <h2 className="text-sm font-semibold uppercase tracking-wider mb-4 px-1" style={{ color: colors.textMuted }}>
              Administration
            </h2>
            <div className="space-y-4">
              <AppCard
                icon={Settings}
                title="Admin Panel"
                description="Manage AI prompts, view analytics, review feedback, and analyze clarification patterns"
                href="/admin"
                color={colors.orange}
              />
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="text-center pt-8 border-t" style={{ borderColor: colors.border }}>
          <p className="text-sm" style={{ color: colors.textMuted }}>
            Built for property management teams
          </p>
        </div>
      </div>
    </div>
  );
}
